import { intl } from '@fullstory/intl';
import { assertExhaustive } from '@fullstory/typeutils';

/**
 * compileRegexValueFormat compiles a regex value format string into a format object.
 * The format string is a sequence of literals and groups.
 * A group is a numeric value that corresponds to a capturing group in the regex (1-indexed).
 * A group can also have a default value that is used if the group is empty (following a '?' character in the group specifier).
 * Example: "(1)some-literal-text(2?default)"
 */
export function compileRegexValueFormat(format: string): {
  format: RegexValueFormat;
  error?: string;
} {
  if (!format) {
    return { format: { type: 'default' } };
  }

  const res: RegexValues = {
    type: 'values',
    values: [],
  };
  let error: string | undefined = undefined;

  let builder = '';
  let escape = false;
  let group = false;
  const commitLiteral = () => {
    if (builder) {
      res.values.push({ type: 'literal', value: builder });
      builder = '';
    }
  };
  const commitGroup = () => {
    if (builder) {
      const parts = builder.split('?', 2);
      const [rawGroup, defaultValue] = parts;
      const group = Number(rawGroup);
      if (isNaN(group)) {
        error = intl.formatMessage({ defaultMessage: 'Invalid group number', id: '1g2NXS' });
      } else {
        res.values.push({
          type: 'group',
          group,
          defaultValue,
        });
      }
      builder = '';
    }
  };

  for (const char of format) {
    if (error) {
      break;
    }
    if (escape) {
      // take this character unconditionally and continue on
      // escaping can happen inside or outside a group
      builder += char;
      escape = false;
      continue;
    }
    switch (char) {
      case '\\':
        escape = true;
        break;
      case '(':
        if (group) {
          error = intl.formatMessage({
            defaultMessage: 'Nested groups are not supported',
            id: 'OyAYT8',
          });
          break;
        }
        commitLiteral();
        group = true;
        break;
      case ')':
        if (!group) {
          error = intl.formatMessage({ defaultMessage: 'Unmatched closing group', id: 'xOGwUh' });
          break;
        }
        commitGroup();
        group = false;
        break;
      default:
        builder += char;
        break;
    }
  }
  if (!error) {
    if (escape) {
      error = intl.formatMessage({ defaultMessage: 'Unmatched escape character', id: 'LkMwUc' });
    } else if (group) {
      error = intl.formatMessage({ defaultMessage: 'Unmatched opening group', id: 'm944Qz' });
    }
  }
  commitLiteral();

  return { format: res, error };
}

export function formatRegexValue(
  format: RegexValueFormat,
  match: string,
  groups: string[],
): string {
  let builder = '';
  switch (format.type) {
    case 'values': {
      for (const value of format.values) {
        builder += formatRegexValue(value, match, groups);
      }
      break;
    }
    case 'default': {
      if (groups.length) {
        // default prefers groups if available
        groups.forEach(g => (builder += g));
      } else {
        // otherwise uses the entire matching string
        builder += match;
      }
      break;
    }
    case 'literal': {
      builder += format.value;
      break;
    }
    case 'group': {
      const gi = format.group - 1;
      const val = groups[gi];
      if (val) {
        builder += val;
      } else if (format.defaultValue) {
        builder += format.defaultValue;
      }
      break;
    }
    default:
      assertExhaustive(format);
  }
  return builder;
}

export type RegexValueFormat =
  | RegexValues
  | RegexValueDefault
  | RegexValueLiteral
  | RegexValueGroup;

export interface RegexValues {
  type: 'values';
  values: RegexValueFormat[];
}

export interface RegexValueDefault {
  type: 'default';
}

export interface RegexValueLiteral {
  type: 'literal';
  value: string;
}

export interface RegexValueGroup {
  type: 'group';
  group: number;
  defaultValue?: string;
}
